import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { Card, CardContent } from '@/components/ui/card'
import { Check } from 'lucide-react'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useForm } from 'react-hook-form'
import { subscribeFormSchema } from './_data/subscribe-definitions'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Separator } from '@/components/ui/separator'
import { statesArray, tiers } from './_data/subscribe-constant'
import { FaFacebookF, FaLinkedinIn, FaYoutube } from 'react-icons/fa'
import { useQuery } from '@tanstack/react-query'
import { getPlanStatusGetPlanStatusPostOptions } from '@/generated/@tanstack/react-query.gen'
import { useAuthStore } from '@/components/PrivateRoute'
import SubscribeLayout from './_component/subscribe-layout'
import Spinner from '@/components/Spinner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { useState } from 'react'
import { callAPI } from '@/hooks/api'
import { useNavigate } from 'react-router'
import { useAuth } from '@/hooks/getAuth'
import TrialPaymentSuccess from '@/components/trial-modal/trial-payment-success'

interface IProps {
  setJoyrideRun: (value: boolean) => void
  setFirstLogin: (value: boolean) => void
}

export default function StripePayment(props: IProps) {
  const { setJoyrideRun, setFirstLogin } = props
  const { isTutorial, isFirstLogin } = useAuthStore((state) => state)
  const [openSuccessModal, setOpenSuccessModal] = useState(false)

  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const auth = useAuthStore((state) => state)
  const navigate = useNavigate()
  const { setIsLoggedIn } = useAuth()

  const currentTier = auth.tier.includes(' - ')
    ? auth.tier.split(' - ')[0]
    : auth.tier
  const tier = tiers.hasOwnProperty(currentTier) ? tiers[currentTier] : null
  const { data, isLoading } = useQuery({
    ...getPlanStatusGetPlanStatusPostOptions({
      body: {
        session_key: auth.sessionKey,
      },
    }),
  })

  const form = useForm<z.infer<typeof subscribeFormSchema>>({
    resolver: zodResolver(subscribeFormSchema),
    defaultValues: {
      terms: false,
      firstName: auth.firstname,
      lastName: auth.lastname,
      email: auth.email,
    },
  })

  const handlePaymentSuccess = () => {
    let localAuth = localStorage.getItem('auth')
    if (localAuth) {
      let authObject = JSON.parse(localAuth)
      authObject.status = 'success'
      localStorage.setItem('auth', JSON.stringify(authObject))
    }
    setIsLoggedIn(true)
    setFirstLogin(isFirstLogin)
    setJoyrideRun(isTutorial)
    setOpenSuccessModal(true)
  }

  async function onSubmit(formValues: z.infer<typeof subscribeFormSchema>) {
    if (!stripe || !elements) {
      return // Ensure Stripe and Elements are loaded
    }

    setLoading(true)
    setError(null) // Clear any previous error

    try {
      // Create new payment method
      const { paymentMethod, error: pmError } =
        // @ts-ignore
        await stripe.createPaymentMethod({
          type: 'card',
          card: elements.getElement('card'),
          billing_details: {
            email: formValues.email,
            name: `${formValues.firstName} ${formValues.lastName}`,
            address: {
              line1: formValues.address,
              city: formValues.city,
              state: formValues.state,
              postal_code: formValues.zip,
            },
          },
        })
      if (pmError) {
        setError(
          pmError.message
            ? pmError.message
            : 'Failed to create payment method. Please try again.'
        )
        setLoading(false)
        return
      }

      // Call backend to create subscription
      const response = await callAPI('/change_plan', {
        session_key: auth.sessionKey,
        email: formValues.email,
        tier: tier?.price_id,
        payment_method_id: paymentMethod.id,
      })

      const { status, clientSecret, subscriptionId } = response

      if (status === 'success') {
        handlePaymentSuccess()
      }

      // Confirm the payment using the client secret
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: paymentMethod.id, // Use the new payment method
        }
      )

      if (error) {
        setError('Payment failed. Please try again.')
      } else if (paymentIntent.status === 'succeeded') {
        handlePaymentSuccess()
      }
    } catch (error) {
      console.error('Error creating subscription:', error)
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false) // Reset loading state
    }
  }

  const handleIconClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const price = tier?.price_annual || tier?.price

  if (!isLoading) {
    return (
      <>
        <SubscribeLayout>
          <div className="text-left max-w-[940px] mb-8 w-full">
            <h1 className="text-white text-3xl font-bold">Subscription</h1>
          </div>
          <Card className="w-full max-w-[940px] mx-auto">
            <CardContent className="p-8 space-y-8 pt-8">
              <div className="space-y-4">
                <h2 className="text-2xl font-bold">{tier?.name}</h2>
                <div className="text-lg text-charcoal font-semibold">
                  {price}
                </div>
                <ul className="space-y-2.5">
                  {tier?.desc.map((feature) => (
                    <li key={feature} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Separator />

              <div>
                <h2 className="text-2xl font-bold mb-6">Billing Info</h2>
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-8"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              First Name{' '}
                              <span className="text-red-500"> *</span>
                            </FormLabel>
                            <FormControl>
                              <Input
                                className="h-14"
                                placeholder="John"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Last Name
                              <span className="text-red-500"> *</span>
                            </FormLabel>
                            <FormControl>
                              <Input
                                className="h-14"
                                placeholder="Doe"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Email
                            <span className="text-red-500"> *</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              className="h-14"
                              type="email"
                              placeholder="john@email.com"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Address
                            <span className="text-red-500"> *</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              className="h-14"
                              placeholder="Street Address"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="additionalAddress"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Additional Address</FormLabel>
                          <FormControl>
                            <Input
                              className="h-14"
                              placeholder="Additional Address"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      <FormField
                        control={form.control}
                        name="state"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              State
                              <span className="text-red-500"> *</span>
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger className="h-14">
                                  <SelectValue placeholder="Select State" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {statesArray.map((state) => (
                                  <SelectItem key={state} value={state}>
                                    {state}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="city"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              City
                              <span className="text-red-500"> *</span>
                            </FormLabel>
                            <FormControl>
                              <Input
                                className="h-14"
                                placeholder="City"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="zip"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              ZIP
                              <span className="text-red-500"> *</span>
                            </FormLabel>
                            <FormControl>
                              <Input
                                className="h-14"
                                placeholder="ZIP Code"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <Separator />
                    <div>
                      <h2 className="text-2xl font-bold mb-3">Payment</h2>
                      <div className="mb-5 text-charcoal">
                        All transactions are secure and encrypted
                      </div>
                      <div className="mb-5">
                        <CardElement className="mb-5" />
                        {error && <div style={{ color: 'red' }}>{error}</div>}

                        <Separator />
                        <div className="mt-5 mb-5">
                          <FormField
                            name="terms"
                            render={({ field }) => (
                              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                  />
                                </FormControl>
                                <div className="relative -top-[5px] text-charcoal">
                                  <span>
                                    I have read and agreed with{' '}
                                    <a
                                      target="_blank"
                                      rel="noreferrer"
                                      href="/terms/Hydra Logic Master Services Agreement.pdf"
                                      className="text-primary hover:underline"
                                    >
                                      Master Service Agreement
                                    </a>
                                    ,{' '}
                                    <a
                                      target="_blank"
                                      rel="noreferrer"
                                      href="https://hydrascore.com/privacy-policy/"
                                      className="text-primary hover:underline"
                                    >
                                      HydraScore Privacy Policy
                                    </a>{' '}
                                    and{' '}
                                    <a
                                      target="_blank"
                                      rel="noreferrer"
                                      href="https://hydrascore.com/terms-and-conditions"
                                      className="text-primary hover:underline"
                                    >
                                      HydraScore Website Terms and Conditions
                                    </a>
                                    .
                                  </span>
                                  <FormMessage />
                                </div>
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      <Button
                        type="submit"
                        disabled={!stripe || !elements || loading}
                        className="w-full md:w-auto"
                      >
                        {loading ? 'Processing...' : 'Confirm Payment'}
                      </Button>
                    </div>
                  </form>
                </Form>
              </div>
            </CardContent>
          </Card>

          <div className="mt-6 text-center">
            <p className="text-gray-400 mb-4">
              Hydra score™ is a precision water and land data analytics
              platform.
            </p>
            <div className="flex justify-center space-x-4">
              {[
                {
                  Icon: FaFacebookF,
                  url: 'https://www.facebook.com/profile.php?id=61560861611781',
                },
                {
                  Icon: FaYoutube,
                  url: 'https://www.youtube.com/channel/UCOfzLw8uh-KvxDKqDjsmBzA',
                },
                {
                  Icon: FaLinkedinIn,
                  url: 'https://www.linkedin.com/company/hydra-score/about/?viewAsMember=true',
                },
              ].map(({ Icon, url }) => (
                <Icon
                  key={url}
                  className="w-5 h-5 text-gray-500 cursor-pointer rounded"
                  onClick={() => handleIconClick(url)}
                />
              ))}
            </div>
          </div>
        </SubscribeLayout>
        <TrialPaymentSuccess
          isOpen={openSuccessModal}
          onOpenChange={(v) => {
            if (!v) {
              setOpenSuccessModal(false)
              window.location.href = '/'
            }
          }}
        />
      </>
    )
  }

  return (
    <SubscribeLayout>
      <Spinner />
    </SubscribeLayout>
  )
}
